import { DateTime } from "luxon";

import { number, minValue, maxLength, required } from "react-admin";
import { Errors, isEmpty, validateMobilePhoneText } from "./validator";
import { IndividualDeliveryWriteBody } from "../api/individual_deliveries";
import { NullReplacedUnassignedDeliveryContent, UnassignedDeliveryContent } from "../api/unassigned_delivery_contents";

export const replaceNullGradeId = (
  unassignedDeliveryContents: UnassignedDeliveryContent[],
  gradeId: number
): NullReplacedUnassignedDeliveryContent[] => {
  return unassignedDeliveryContents.map((udc) => {
    if (udc.grade_id === null) {
      return {
        grade_id: gradeId,
        package_count: udc.package_count,
      };
    }
    // HACK: 単純にudcをreturnすると型チェックに引っかかるので一度展開して返す
    return {
      grade_id: udc.grade_id,
      package_count: udc.package_count,
    };
  });
};

function greaterThanPossibleDate(date: string, possibleDate: string) {
  const dateTime = DateTime.fromISO(date);
  const handoverPossibleDateTime = DateTime.fromISO(possibleDate);
  if (!handoverPossibleDateTime.isValid) {
    // 引取可能日は必ず設定されているのでvalidでない場合はエラー
    throw new Error("handover possible date is empty");
  }
  return handoverPossibleDateTime <= dateTime;
}

export function getValidateDateIsAfterHandoverAt(
  handover_possible_date: string | null,
  isSpotContract: boolean = false
) {
  return (value: string | undefined | null) => {
    if (value === undefined || value === null || value === "" || handover_possible_date === null) {
      return null;
    }

    if (!greaterThanPossibleDate(value, handover_possible_date)) {
      return isSpotContract
        ? "validation.individual_deliveries.can_not_set_before_decision_date"
        : "validation.individual_deliveries.can_not_set_before_possible_date";
    }

    return null;
  };
}

const validatePackageCount = [required(), number(), minValue(1)];

// validatePackageCountを使用した関数を追加
export function validatePackageCountValue(packageCount: number): string | null {
  // react-adminのバリデーション関数を順番に実行
  for (const validator of validatePackageCount) {
    const result = validator(packageCount);
    if (result) {
      return result;
    }
  }
  return null;
}

// ヘルパー関数を追加
function validateMaxLength(value: string | undefined, maxLen: number): string | null {
  if (!value) return null;
  const result = maxLength(maxLen)(value);
  return result || null;
}

export function validateHacobellInputs(
  values: IndividualDeliveryWriteBody,
  handover_possible_date: string | null
): Errors {
  const errors: Errors = {};

  const { hacobell } = values;
  if (hacobell === null || hacobell === undefined) {
    return errors;
  }

  const { customer_name, arrival_preferred_from, arrival_preferred_to, shipment_notes } = hacobell;

  // customer_name: 最大文字数チェック（50文字）
  const customerNameError = validateMaxLength(customer_name, 50);
  if (customerNameError) {
    errors["customer_name"] = customerNameError;
  }

  // arrival_preferred_from: 引取可能日以降かどうかのチェック
  if (arrival_preferred_from && handover_possible_date) {
    const result = getValidateDateIsAfterHandoverAt(handover_possible_date)(arrival_preferred_from);
    if (result) {
      errors["arrival_preferred_from"] = result;
    }
  }

  // arrival_preferred_to: 引取可能日以降かどうかのチェック
  if (arrival_preferred_to && handover_possible_date) {
    const result = getValidateDateIsAfterHandoverAt(handover_possible_date)(arrival_preferred_to);
    if (result) {
      errors["arrival_preferred_to"] = result;
    }
  }

  // shipment_notes: 最大文字数チェック（2048文字）
  const shipmentNotesError = validateMaxLength(shipment_notes, 2048);
  if (shipmentNotesError) {
    errors["shipment_notes"] = shipmentNotesError;
  }

  return errors;
}

export function validateSelfDeliveryInputs(
  values: IndividualDeliveryWriteBody,
  handover_possible_date: string | null
): Errors {
  const errors: Errors = {};

  const { self_delivery } = values;
  if (self_delivery === null || self_delivery === undefined) {
    return errors;
  }

  const { handover_date, handover_time, shipping_company_name, driver_name, driver_phone_number, car_number } =
    self_delivery;

  // handover_date: 引取可能日以降かどうかのチェック
  if (handover_date && handover_possible_date) {
    const result = getValidateDateIsAfterHandoverAt(handover_possible_date)(handover_date);
    if (result) {
      errors["handover_date"] = result;
    }
  }

  // handover_time: 最大文字数チェック（20文字）
  const handoverTimeError = validateMaxLength(handover_time, 20);
  if (handoverTimeError) {
    errors["handover_time"] = handoverTimeError;
  }

  // shipping_company_name: 最大文字数チェック（50文字）
  const shippingCompanyError = validateMaxLength(shipping_company_name, 50);
  if (shippingCompanyError) {
    errors["shipping_company_name"] = shippingCompanyError;
  }

  // driver_name: 最大文字数チェック（50文字）
  const driverNameError = validateMaxLength(driver_name, 50);
  if (driverNameError) {
    errors["driver_name"] = driverNameError;
  }

  // driver_phone_number: 最大文字数チェック（13文字）+ 携帯電話番号形式チェック
  if (driver_phone_number) {
    const maxLengthResult = validateMaxLength(driver_phone_number, 13);
    if (maxLengthResult) {
      errors["driver_phone_number"] = maxLengthResult;
    } else {
      const mobilePhoneResult = validateMobilePhoneText(driver_phone_number);
      if (mobilePhoneResult) {
        errors["driver_phone_number"] = mobilePhoneResult;
      }
    }
  }

  // car_number: 最大文字数チェック（20文字）
  const carNumberError = validateMaxLength(car_number, 20);
  if (carNumberError) {
    errors["car_number"] = carNumberError;
  }

  return errors;
}

function validateHacobell(values: IndividualDeliveryWriteBody): Errors {
  const errors: Errors = {};

  const { hacobell } = values;
  if (hacobell === null || hacobell === undefined) {
    // 何もフォームをさわらなかった場合
    errors["arrival_preferred_from"] = "ra.validation.required";
    errors["arrival_preferred_to"] = "ra.validation.required";
    errors["preferred_vehicle"] = "ra.validation.required";
    return errors;
  }

  const { arrival_preferred_from, arrival_preferred_to, preferred_vehicle } = hacobell;

  if (arrival_preferred_from && arrival_preferred_to) {
    const fromDateTime = DateTime.fromISO(arrival_preferred_from);
    const toDateTime = DateTime.fromISO(arrival_preferred_to);
    if (toDateTime < fromDateTime) {
      errors["arrival_preferred_from"] = "validation.individual_deliveries.can_not_set_after_to_datetime";
      errors["arrival_preferred_to"] = "validation.individual_deliveries.can_not_set_before_from_datetime";
    }
  } else {
    if (!arrival_preferred_from) {
      errors["arrival_preferred_from"] = "ra.validation.required";
    }
    if (!arrival_preferred_to) {
      errors["arrival_preferred_to"] = "ra.validation.required";
    }
  }

  if (!preferred_vehicle) {
    errors["preferred_vehicle"] = "ra.validation.required";
  }

  return errors;
}

function validateSelfDelivery(values: IndividualDeliveryWriteBody): Errors {
  const errors: Errors = {};

  const { self_delivery } = values;
  if (self_delivery === null || self_delivery === undefined) {
    // 何もフォームをさわらなかった場合
    errors["handover_date"] = "ra.validation.required";
    return errors;
  }

  const { handover_date } = self_delivery;

  // 必須以外のチェックは個別のフォーム項目自体のvalidationでやる
  if (!handover_date) {
    errors["handover_date"] = "ra.validation.required";
  }

  return errors;
}

type RowError = { [key: string]: string };

function validateDeliveryContents(
  values: IndividualDeliveryWriteBody,
  unassignedDeliveryContents: UnassignedDeliveryContent[],
  gradeId: number
): null | string | Array<RowError> {
  const { delivery_contents } = values;
  if (delivery_contents !== undefined && delivery_contents !== null && delivery_contents.length !== 0) {
    const filteredDeliveryContents = delivery_contents.filter((dc) => !!dc);
    const allGrades = filteredDeliveryContents.map((dc) => dc.grade_id);

    const deliveryContentErrors: Array<RowError | {}> = [];
    filteredDeliveryContents.forEach((dc) => {
      const rowErrors: RowError = {};
      const gradeCounts = allGrades.filter((ag) => ag === dc.grade_id).length;
      if (gradeCounts > 1) {
        rowErrors.grade_id = "validation.individual_deliveries.grade_is_duplicated";
      }
      const gradeIdReplacedDeliveryContents = replaceNullGradeId(unassignedDeliveryContents, gradeId);
      const unassignedCount = gradeIdReplacedDeliveryContents.find((uc) => uc.grade_id === dc?.grade_id);
      if (!unassignedCount) {
        // 通常は起きないはず
        rowErrors.package_count = "validation.individual_deliveries.can_not_load_package_count";
      } else if (unassignedCount.package_count < dc.package_count) {
        rowErrors.package_count = "validation.individual_deliveries.over_package_count";
      }
      if (isEmpty(rowErrors)) {
        deliveryContentErrors.push({});
      } else {
        deliveryContentErrors.push(rowErrors);
      }
    });

    if (deliveryContentErrors.some((dce) => !isEmpty(dce))) {
      return deliveryContentErrors;
    }
  }
  return null;
}

export function getValidateIndividualDelivery(
  unassignedDeliveryContents: UnassignedDeliveryContent[],
  gradeId: number,
  isSubmitActionRef: React.MutableRefObject<boolean>,
  handover_possible_date?: string | null,
  isPublished?: boolean
) {
  return (values: IndividualDeliveryWriteBody) => {
    let errors: Errors = {};

    const { delivery_address_id, delivery_contents, delivery_type, status } = values;

    if (delivery_contents && delivery_contents.length > 0) {
      delivery_contents.forEach((dc, index) => {
        const packageCountError = validatePackageCountValue(dc.package_count);
        if (packageCountError) {
          errors[`delivery_contents.${index}.package_count`] = packageCountError;
        }
      });
    }

    // 個別のバリデーション（下書きであるかどうかに関わらず表示）
    switch (delivery_type) {
      // ハコベルの場合
      case "hacobell": {
        const hacobellErrors = validateHacobellInputs(values, handover_possible_date || null);
        if (!isEmpty(hacobellErrors)) {
          errors = {
            ...errors,
            hacobell: hacobellErrors,
          };
        }
        break;
      }
      // 自社運送の場合
      case "self_delivery": {
        const selfDeliveryErrors = validateSelfDeliveryInputs(values, handover_possible_date || null);
        if (!isEmpty(selfDeliveryErrors)) {
          errors = {
            ...errors,
            self_delivery: selfDeliveryErrors,
          };
        }
        break;
      }
      default: {
        break;
      }
    }

    const deliveryContentsErrors = validateDeliveryContents(values, unassignedDeliveryContents, gradeId);
    if (!isPublished && deliveryContentsErrors) {
      errors = {
        ...errors,
        delivery_contents: deliveryContentsErrors,
      };
    }

    // 個別バリデーションのみ
    if (!isSubmitActionRef.current) {
      return errors;
    }

    // 下書きの場合
    if (status === "draft") {
      if (delivery_contents !== undefined && delivery_contents !== null && delivery_contents.length !== 0) {
        const filteredDeliveryContents = delivery_contents.filter((dc) => !!dc);
        errors["delivery_contents"] = filteredDeliveryContents.map(() => {
          return {
            grade_id: "validation.individual_deliveries.draft_can_not_set_delivery_contents",
            package_count: "validation.individual_deliveries.draft_can_not_set_delivery_contents",
          };
        });
      }

      return errors;
    }

    // それ以外の場合
    if (delivery_address_id === undefined || delivery_address_id === null) {
      errors["delivery_address_id"] = "ra.validation.required";
    }

    if (delivery_contents === undefined || delivery_contents === null || delivery_contents.length === 0) {
      errors = {
        ...errors,
        delivery_contents: "ra.validation.required",
      };
    }

    switch (delivery_type) {
      // ハコべルの場合
      case "hacobell": {
        const hacobellErrors = validateHacobell(values);
        if (!isEmpty(hacobellErrors)) {
          errors = {
            ...errors,
            hacobell: hacobellErrors,
          };
        }
        break;
      }
      // 自社運送の場合
      case "self_delivery": {
        const selfDeliveryErrors = validateSelfDelivery(values);
        if (!isEmpty(selfDeliveryErrors)) {
          errors = {
            ...errors,
            self_delivery: selfDeliveryErrors,
          };
        }
        break;
      }
      default: {
        break;
      }
    }
    return errors;
  };
}
